import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Heart, Scales, ArrowRight, EnvelopeSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import SiteNav from "@/components/SiteNav";
import PropertyCard from "@/components/PropertyCard";
import useFavorites from "@/hooks/useFavorites";
import api, { formatApiErrorDetail } from "@/lib/apiClient";
import { formatPrice, formatListingPrice } from "@/lib/constants";
import ListingImage from "@/components/ListingImage";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import Seo from "@/lib/seo";
import { track } from "@/lib/tracking";

const ROWS = [
  { label: "Price", get: (p) => formatListingPrice(p.price, p.listing_type) },
  { label: "Type", get: (p) => p.property_type },
  { label: "Buy/Rent", get: (p) => (p.listing_type === "rent" ? "For Rent" : "For Sale") },
  { label: "Area", get: (p) => p.neighborhood || p.region },
  { label: "Bedrooms", get: (p) => p.bedrooms },
  { label: "Bathrooms", get: (p) => p.bathrooms },
  { label: "Sq. ft.", get: (p) => (p.sqft ? p.sqft.toLocaleString() : "—") },
  { label: "$ / sq.ft.", get: (p) => (p.sqft && p.listing_type !== "rent" ? formatPrice(Math.round(p.price / p.sqft)) : "—") },
  { label: "No fee", get: (p) => (p.listing_type === "rent" ? (p.no_fee ? "Yes" : "—") : "—") },
  { label: "Pets", get: (p) => (p.pets_allowed ? "Allowed" : "—") },
  { label: "Status", get: (p) => (p.status || "active") },
];

export default function Favorites() {
  const { favorites } = useFavorites();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [compareOpen, setCompareOpen] = useState(false);
  const [shortlistEmail, setShortlistEmail] = useState("");
  const [shortlistName, setShortlistName] = useState("");
  const [sendingShortlist, setSendingShortlist] = useState(false);
  const [shortlistSent, setShortlistSent] = useState(false);
  const key = favorites.join(",");

  const emailShortlist = async (e) => {
    e.preventDefault();
    if (!shortlistEmail) {
      toast.error("Enter your email");
      return;
    }
    setSendingShortlist(true);
    try {
      await api.post("/favorites/shortlist", {
        email: shortlistEmail,
        name: shortlistName || undefined,
        property_ids: favorites,
      });
      setShortlistSent(true);
      track("shortlist", { count: favorites.length });
      toast.success("Shortlist sent — you'll also get price-drop alerts on these homes");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not send shortlist");
    } finally {
      setSendingShortlist(false);
    }
  };

  useEffect(() => {
    let on = true;
    setLoading(true);
    Promise.all(favorites.map((id) => api.get(`/properties/${id}`).then((r) => r.data).catch(() => null)))
      .then((res) => { if (on) { setItems(res.filter(Boolean)); setLoading(false); } });
    return () => { on = false; };
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="bg-background min-h-screen">
      <Seo
        title="Saved Homes"
        description="Your saved-home shortlist at The Whyte Glove — compare homes and get price-drop alerts."
        path="/favorites"
      />
      <SiteNav />
      <div className="max-w-7xl mx-auto px-6 pt-28 pb-16">
        <div className="mb-8 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div>
            <p className="text-brand text-xs uppercase tracking-[0.3em] mb-3">Your shortlist</p>
            <h1 className="text-4xl md:text-5xl text-white flex items-center gap-3">
              <Heart size={34} weight="fill" className="text-brand" /> Saved homes
            </h1>
            <p className="text-white/50 mt-2">{loading ? "Loading…" : `${items.length} saved`}</p>
          </div>
          {items.length >= 2 && (
            <button
              onClick={() => setCompareOpen(true)}
              data-testid="compare-btn"
              className="flex items-center gap-2 rounded-sm bg-brand px-5 py-2.5 text-sm font-medium uppercase tracking-wider text-black hover:bg-brand-hover"
            >
              <Scales size={17} weight="duotone" /> Compare {items.length}
            </button>
          )}
        </div>

        {loading ? (
          <div className="py-24 flex justify-center"><div className="h-8 w-8 rounded-full border-2 border-brand border-t-transparent animate-spin" /></div>
        ) : items.length === 0 ? (
          <div className="py-24 text-center" data-testid="favorites-empty">
            <Heart size={40} weight="duotone" className="mx-auto text-white/20 mb-4" />
            <p className="text-white/50 mb-4">No saved homes yet. Tap the heart on any listing to save it here.</p>
            <Link to="/listings" className="inline-flex items-center gap-2 text-brand text-sm hover:text-brand-hover">
              Browse homes <ArrowRight size={15} weight="bold" />
            </Link>
          </div>
        ) : (
          <>
            {!shortlistSent ? (
              <form
                onSubmit={emailShortlist}
                data-testid="shortlist-form"
                className="mb-8 rounded-sm border border-brand/25 bg-brand/5 p-5 flex flex-col md:flex-row md:items-center gap-3"
              >
                <p className="text-white/70 text-sm md:mr-2 flex items-center gap-2 shrink-0">
                  <EnvelopeSimple size={18} className="text-brand" /> Email me this shortlist + price-drop alerts
                </p>
                <input
                  data-testid="shortlist-name"
                  className="lux-input md:max-w-[180px]"
                  placeholder="Name (optional)"
                  value={shortlistName}
                  onChange={(e) => setShortlistName(e.target.value)}
                />
                <input
                  data-testid="shortlist-email"
                  type="email"
                  className="lux-input md:max-w-[260px]"
                  placeholder="you@email.com"
                  value={shortlistEmail}
                  onChange={(e) => setShortlistEmail(e.target.value)}
                />
                <button
                  type="submit"
                  disabled={sendingShortlist}
                  data-testid="shortlist-submit"
                  className="bg-brand text-black px-6 py-3 rounded-sm text-xs uppercase tracking-wider font-medium hover:bg-brand-hover disabled:opacity-60 shrink-0"
                >
                  {sendingShortlist ? "Sending…" : "Send my list"}
                </button>
              </form>
            ) : (
              <p className="mb-8 text-sm text-brand" data-testid="shortlist-done">
                Shortlist sent — you'll get alerts if any of these drop in price or come back on the market.
              </p>
            )}
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="favorites-grid">
              {items.map((p) => <PropertyCard key={p.id} property={p} />)}
            </div>
          </>
        )}
      </div>

      <Dialog open={compareOpen} onOpenChange={setCompareOpen}>
        <DialogContent className="bg-[#101116] border-white/10 text-white max-w-4xl" data-testid="compare-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl flex items-center gap-2">
              <Scales size={22} weight="duotone" className="text-brand" /> Compare homes
            </DialogTitle>
          </DialogHeader>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr>
                  <th className="sticky left-0 bg-[#101116]" />
                  {items.map((p) => (
                    <th key={p.id} className="p-2 align-top min-w-[160px]">
                      <ListingImage
                        src={p.images?.[0]}
                        alt={p.title}
                        className="h-24 w-full object-cover rounded-sm mb-2"
                      />
                      <Link to={`/listings/${p.id}`} className="text-white hover:text-brand text-xs leading-tight block">{p.title}</Link>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {ROWS.map((row) => (
                  <tr key={row.label} className="border-t border-white/5">
                    <td className="sticky left-0 bg-[#101116] py-2.5 pr-4 text-white/40 text-xs uppercase tracking-wider whitespace-nowrap">{row.label}</td>
                    {items.map((p) => (
                      <td key={p.id} className="py-2.5 px-2 text-white/85">{row.get(p)}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
